import React, { useState, useEffect, useCallback, useMemo } from 'react';
// jspdf is lazy-loaded inside exportPDF — see Sales.js for rationale.
import { fetchWithAuth } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { formatNumber, buildCSV } from '../../utils/helpers';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import SortSelector from '../common/SortSelector';
import ConfirmationModal from '../modals/ConfirmationModal';
import MortalityAnalytics from '../analytics/MortalityAnalytics';
import { 
  PlusCircle, 
  Edit, 
  X
} from 'lucide-react';

function Mortality({ user, handleLogout, targetMortalityId, clearTargetMortality }) {
  const toast = useToast();
  const [mortalities, setMortalities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingMortality, setEditingMortality] = useState(null);
  const [error, setError] = useState('');
  const [layoutMode, setLayoutMode] = useState(window.innerWidth < 768 ? 'cards' : 'table');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [archiveConfirm, setArchiveConfirm] = useState({ isOpen: false, ids: [], archived: false });
  const [showFilters, setShowFilters] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    location: '',
    employee: '',
    archived: 'unarchived'
  });

  // Multi-row form state
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkRecords, setBulkRecords] = useState([{ location_id: '', location_search: '', employee_id: '', employee_search: '', deaths: '', feeds: '', notes: '' }]);
  const [lockLocation, setLockLocation] = useState(false);
  const [lockedLocation, setLockedLocation] = useState({ id: '', name: '' });

  // Each fetcher accepts an optional AbortSignal so the mount-effect can
  // cancel in-flight requests on unmount / dep change.
  const fetchMortalities = useCallback((signal) => {
    return fetchWithAuth('/mortality', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setMortalities(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchLocations = useCallback((signal) => {
    return fetchWithAuth('/locations', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setLocations(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  const fetchEmployees = useCallback((signal) => {
    return fetchWithAuth('/employees', { signal }, user, handleLogout)
      .then(res => res.json())
      .then(data => setEmployees(data || []))
      .catch(err => { if (err?.name !== 'AbortError') console.error(err); });
  }, [user, handleLogout]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    Promise.allSettled([
      fetchMortalities(controller.signal),
      fetchLocations(controller.signal),
      fetchEmployees(controller.signal),
    ]).then(() => setIsLoading(false));
    return () => controller.abort();
  }, [fetchMortalities, fetchLocations, fetchEmployees]);

  useEffect(() => {
    if (targetMortalityId && mortalities.length > 0) {
      const target = mortalities.find(m => m.id === targetMortalityId);
      if (target) {
        handleEditClick(target);
      }
      if (clearTargetMortality) clearTargetMortality();
    }
  }, [targetMortalityId, mortalities, clearTargetMortality]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rowsPerPage, sortConfig]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedMortalities.map(m => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleArchive = (ids, archivedStatus) => {
    if (!ids || ids.length === 0) return;
    setArchiveConfirm({ isOpen: true, ids, archived: archivedStatus });
  };

  const performArchive = () => {
    const { ids, archived: archivedStatus } = archiveConfirm;
    setArchiveConfirm({ isOpen: false, ids: [], archived: false });

    fetchWithAuth('/archive', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'mortality',
        ids: ids,
        archived: archivedStatus
      })
    }, user, handleLogout)
    .then(res => {
      if (!res.ok) throw new Error('Failed to update archive status');
      fetchMortalities();
      setSelectedIds([]);
      toast.success(archivedStatus ? 'Records archived.' : 'Records restored.');
    })
    .catch(err => toast.error(err.message));
  };

  const filteredMortalities = useMemo(() => {
    return mortalities.filter(m => {
      if (filters.startDate && new Date(m.date).toISOString().split('T')[0] < filters.startDate) return false;
      if (filters.endDate && new Date(m.date).toISOString().split('T')[0] > filters.endDate) return false;
      if (filters.location && !m.location_name.toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (filters.employee && !m.employee_name.toLowerCase().includes(filters.employee.toLowerCase())) return false;
      if (filters.archived === 'unarchived' && m.archived) return false;
      if (filters.archived === 'archived' && !m.archived) return false;
      return true;
    });
  }, [mortalities, filters]);

  const sortedMortalities = useMemo(() => {
    let sortableItems = [...filteredMortalities];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'location') {
          aVal = (a.location_name || '').toLowerCase();
          bVal = (b.location_name || '').toLowerCase();
        } else if (sortConfig.key === 'employee') {
          aVal = (a.employee_name || '').toLowerCase();
          bVal = (b.employee_name || '').toLowerCase();
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredMortalities, sortConfig]);

  const paginatedMortalities = useMemo(() => {
    if (rowsPerPage === 'all') return sortedMortalities;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedMortalities.slice(start, start + rowsPerPage);
  }, [sortedMortalities, currentPage, rowsPerPage]);

  const totalDeaths = filteredMortalities.reduce((sum, m) => sum + m.number_of_deaths, 0);
  const totalFeeds = filteredMortalities.reduce((sum, m) => sum + m.number_of_feeds, 0);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleBulkInputChange = (index, field, value) => {
    const newRecords = [...bulkRecords];
    const updatedRow = { ...newRecords[index] };
    
    if (field === 'location_search') {
      const loc = locations.find(l => l.name === value);
      updatedRow.location_id = loc ? loc.id : '';
      updatedRow.location_search = value;
    } else if (field === 'employee_search') {
      const emp = employees.find(e => `${e.first_name} ${e.last_name}` === value);
      updatedRow.employee_id = emp ? emp.id : '';
      updatedRow.employee_search = value;
    } else {
      updatedRow[field] = value;
    }
    
    newRecords[index] = updatedRow;
    setBulkRecords(newRecords);
  };

  const addBulkRow = () => {
    const newRow = { 
      location_id: lockLocation ? lockedLocation.id : '', 
      location_search: lockLocation ? lockedLocation.name : '', 
      employee_id: '', 
      employee_search: '', 
      deaths: '', 
      feeds: '',
      notes: ''
    };
    setBulkRecords([...bulkRecords, newRow]);
  };

  const removeBulkRow = (index) => {
    if (bulkRecords.length === 1) return;
    setBulkRecords(bulkRecords.filter((_, i) => i !== index));
  };

  const checkDuplicates = () => {
    const dateStr = bulkDate;
    for (const r of bulkRecords) {
      if (!r.location_id || !r.employee_id) continue;
      const exists = mortalities.find(m => 
        m.date.slice(0, 10) === dateStr && 
        m.location_id === parseInt(r.location_id) && 
        m.employee_id === parseInt(r.employee_id)
      );
      if (exists) {
        return { exists: true, record: exists };
      }
    }
    return { exists: false };
  };

  const handleBulkSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');

    const duplicate = checkDuplicates();
    if (duplicate.exists) {
      setError(`Record already exists for ${duplicate.record.location_name} and ${duplicate.record.employee_name} on this date. Please edit the existing record instead.`);
      return;
    }

    const records = bulkRecords
      .filter(r => r.location_id && r.employee_id)
      .map(r => ({
        location_id: parseInt(r.location_id),
        employee_id: parseInt(r.employee_id),
        number_of_deaths: parseInt(r.deaths) || 0,
        number_of_feeds: parseInt(r.feeds) || 0,
        notes: r.notes
      }));

    if (records.length === 0) {
      setError('Please provide at least one complete mortality record (Location and Employee are required).');
      return;
    }

    setIsSubmitting(true);
    fetchWithAuth('/mortality/create_bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: new Date(bulkDate).toISOString(), records })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) {
        if (res.status === 409) {
          throw new Error("One or more records already exist for this date/location/employee combination. Please edit them instead.");
        }
        throw new Error(await res.text());
      }
      fetchMortalities();
      setShowRecordModal(false);
      setBulkRecords([{ location_id: '', location_search: '', employee_id: '', employee_search: '', deaths: '', feeds: '', notes: '' }]);
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleEditClick = (m) => {
    setEditingMortality({
      ...m, 
      date: new Date(m.date).toISOString().slice(0, 10), 
      location_search: m.location_name, 
      employee_search: m.employee_name, 
      archived: m.archived || false
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    setIsSubmitting(true);
    fetchWithAuth('/mortality/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editingMortality,
        location_id: parseInt(editingMortality.location_id),
        employee_id: parseInt(editingMortality.employee_id),
        number_of_deaths: parseInt(editingMortality.number_of_deaths),
        number_of_feeds: parseInt(editingMortality.number_of_feeds),
        date: new Date(editingMortality.date).toISOString(),
        archived: !!editingMortality.archived
      })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchMortalities();
      setEditingMortality(null);
    })
    .catch(err => setError(err.message))
    .finally(() => setIsSubmitting(false));
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = () => {
    fetchWithAuth(`/mortality/delete?id=${deleteConfirm.id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchMortalities();
      setDeleteConfirm({ isOpen: false, id: null });
      toast.success('Record deleted.');
    })
    .catch(err => toast.error(err.message));
  };

  const exportCSV = () => {
    const headers = ['Date', 'Location', 'Employee', 'Deaths', 'Feeds', 'Added By', 'Notes'];
    const rows = filteredMortalities.map(m => [
      new Date(m.date).toLocaleDateString(),
      m.location_name,
      m.employee_name,
      m.number_of_deaths,
      m.number_of_feeds,
      m.added_by,
      m.notes || ''
    ]);

    const csvContent = buildCSV([headers, ...rows]);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `mortality_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    const doc = new jsPDF('landscape');
    doc.text("Mortality tracking Report", 14, 15);
    
    const tableColumn = ["Date", "Location", "Employee", "Deaths", "Feeds", "Added By", "Notes"];
    const tableRows = filteredMortalities.map(m => [
      new Date(m.date).toLocaleDateString(),
      m.location_name,
      m.employee_name,
      m.number_of_deaths,
      m.number_of_feeds,
      m.added_by,
      m.notes || ''
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });
    doc.save(`mortality_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="mortality-view">
      <div className="view-header">
        <h2>Mortality Tracking</h2>
        <div className="view-actions">
          <button className="secondary-btn no-print" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button className="submit-btn no-print" onClick={() => setShowRecordModal(true)}>
            Add Mortality Record
          </button>
          <button className="secondary-btn no-print" onClick={() => setShowAnalytics(!showAnalytics)}>
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          <button className="action-btn no-print" onClick={exportCSV}>
            Download CSV
          </button>
          <button className="action-btn no-print" onClick={exportPDF}>
            Download PDF
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <h4>Filters</h4>
          <div className="filter-grid">
            <div className="filter-group">
              <label>Date Range</label>
              <div className="date-range-inputs">
                <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} />
                <span>to</span>
                <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} />
              </div>
            </div>
            <div className="filter-group">
              <label>Location</label>
              <input 
                list="filter-mort-loc-list"
                placeholder="Search location..." 
                value={filters.location} 
                onChange={(e) => handleFilterChange('location', e.target.value)} 
              />
              <datalist id="filter-mort-loc-list">
                {locations.map(l => <option key={l.id} value={l.name} />)}
              </datalist>
            </div>
            <div className="filter-group">
              <label>Employee</label>
              <input 
                list="filter-mort-emp-list"
                placeholder="Search employee..." 
                value={filters.employee} 
                onChange={(e) => handleFilterChange('employee', e.target.value)} 
              />
              <datalist id="filter-mort-emp-list">
                {employees.map(e => <option key={e.id} value={`${e.first_name} ${e.last_name}`} />)}
              </datalist>
            </div>
            <div className="filter-group">
              <label>Archive Status</label>
              <select value={filters.archived} onChange={(e) => handleFilterChange('archived', e.target.value)}>
                <option value="unarchived">Unarchived Only</option>
                <option value="archived">Archived Only</option>
                <option value="all">All Records</option>
              </select>
            </div>
          </div>
          <div className="filter-actions">
            <button onClick={() => setFilters({ startDate: '', endDate: '', location: '', employee: '', archived: 'unarchived' })} className="secondary-btn">Clear All Filters</button>
          </div>
        </div>
      )}

      {showAnalytics && (
        <MortalityAnalytics mortalities={filteredMortalities} />
      )}

      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
          {layoutMode === 'cards' && (
            <SortSelector 
              options={[
                { key: 'date', label: 'Date' },
                { key: 'location', label: 'Location' },
                { key: 'employee', label: 'Employee' },
                { key: 'number_of_deaths', label: 'Deaths' },
                { key: 'number_of_feeds', label: 'Feeds' }
              ]} 
              sortConfig={sortConfig} 
              onSortChange={setSortConfig} 
            />
          )}
        </div>
        <div className="layout-toggle no-print">
          <button className={layoutMode === 'table' ? 'active' : ''} onClick={() => setLayoutMode('table')}>Table</button>
          <button className={layoutMode === 'cards' ? 'active' : ''} onClick={() => setLayoutMode('cards')}>Cards</button>
        </div>
      </div>

      {isLoading && <div className="loading-banner">Loading mortality records…</div>}

      {layoutMode === 'table' ? (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th><input
                  type="checkbox"
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < paginatedMortalities.length; }}
                  onChange={handleSelectAll}
                  checked={paginatedMortalities.length > 0 && selectedIds.length === paginatedMortalities.length}
                /></th>
                <th onClick={() => requestSort('date')} className="sortable">Date {sortConfig.key === 'date' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('location')} className="sortable">Location {sortConfig.key === 'location' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('employee')} className="sortable">Employee {sortConfig.key === 'employee' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('number_of_deaths')} className="sortable text-right">Deaths {sortConfig.key === 'number_of_deaths' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th onClick={() => requestSort('number_of_feeds')} className="sortable text-right">Feeds {sortConfig.key === 'number_of_feeds' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMortalities.map(m => (
                <tr key={m.id} className={`clickable-row ${m.archived ? 'archived-row' : ''} ${selectedIds.includes(m.id) ? 'selected-row' : ''}`} onClick={() => handleEditClick(m)}>
                  <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.includes(m.id)} onChange={() => handleSelectOne(m.id)} /></td>
                  <td>{new Date(m.date).toLocaleDateString()} {m.archived && <span className="archived-tag">Archived</span>}</td>
                  <td>{m.location_name}</td>
                  <td>{m.employee_name}</td>
                  <td className="text-right status-error">{formatNumber(m.number_of_deaths)}</td>
                  <td className="text-right">{formatNumber(m.number_of_feeds)}</td>
                  <td className="notes-cell" title={m.notes}>{m.notes || '-'}</td>
                  <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="action-btn sm" onClick={() => handleEditClick(m)}>Edit</button>
                    <button className="secondary-btn sm" onClick={() => handleArchive([m.id], !m.archived)}>{m.archived ? 'Restore' : 'Archive'}</button>
                    {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDelete(m.id)}>Del</button>}
                  </td>
                </tr>
              ))}
              <tr className="totals-row">
                <td colSpan="4">Totals</td>
                <td className="text-right status-error"><strong>{formatNumber(totalDeaths)}</strong></td>
                <td className="text-right"><strong>{formatNumber(totalFeeds)}</strong></td>
                <td colSpan="2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card-grid">
          {paginatedMortalities.map(m => (
            <div key={m.id} className={`data-card mortality-card clickable-row ${m.archived ? 'archived-row' : ''} ${selectedIds.includes(m.id) ? 'selected-card' : ''}`} onClick={() => handleEditClick(m)}>
              <div className="card-header">
                <div className="card-title-group">
                  <input type="checkbox" checked={selectedIds.includes(m.id)} onClick={(e) => e.stopPropagation()} onChange={() => handleSelectOne(m.id)} />
                  <h4>{m.location_name} {m.archived && <span className="archived-tag">Archived</span>}</h4>
                </div>
                <span className="card-date">{new Date(m.date).toLocaleDateString()}</span>
              </div>
              <div className="card-body">
                <div className="card-item"><span className="label">Employee</span><span className="value">{m.employee_name}</span></div>
                <div className="card-item"><span className="label">Deaths</span><span className="value status-error">{formatNumber(m.number_of_deaths)}</span></div>
                <div className="card-item"><span className="label">Feeds</span><span className="value">{formatNumber(m.number_of_feeds)}</span></div>
                <div className="card-item"><span className="label">Added By</span><span className="value">{m.added_by}</span></div>
                <div className="card-item full-width">
                  <span className="label">Notes</span>
                  <span className="value notes-text">{m.notes || 'None'}</span>
                </div>
              </div>
              <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="action-btn sm" onClick={() => handleEditClick(m)}>Edit</button>
                <button className="secondary-btn sm" onClick={() => handleArchive([m.id], !m.archived)}>{m.archived ? 'Restore' : 'Archive'}</button>
                {user.role === 'admin' && <button className="cancel-btn sm" onClick={() => handleDelete(m.id)}>Del</button>}
              </div>
            </div>
          ))}
          <div className="data-card summary-card">
            <h4>Totals Summary</h4>
            <div className="card-body">
              <div className="card-item"><span className="label">Total Records</span><span className="value">{formatNumber(filteredMortalities.length)}</span></div>
              <div className="card-item"><span className="label">Total Deaths</span><span className="value status-error">{formatNumber(totalDeaths)}</span></div>
              <div className="card-item"><span className="label">Total Feeds</span><span className="value highlight">{formatNumber(totalFeeds)}</span></div>
            </div>
          </div>
        </div>
      )}

      <Pagination totalItems={filteredMortalities.length} itemsPerPage={rowsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar no-print">
          <span className="selection-count">{selectedIds.length} items selected</span>
          <div className="bulk-actions">
            {filters.archived !== 'archived' && <button className="secondary-btn" onClick={() => handleArchive(selectedIds, true)}>Archive Selected</button>}
            {filters.archived !== 'unarchived' && <button className="submit-btn" onClick={() => handleArchive(selectedIds, false)}>Restore Selected</button>}
            <button className="cancel-btn" onClick={() => setSelectedIds([])}>Clear Selection</button>
          </div>
        </div>
      )}

      {showRecordModal && (
        <div className="modal" onClick={() => setShowRecordModal(false)}>
          <div className="modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <PlusCircle size={24} />
                <h3>Add Mortality Records</h3>
              </div>
              <button className="close-btn" onClick={() => setShowRecordModal(false)}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleBulkSubmit}>
              <div className="bulk-header-controls">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} required />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={lockLocation} onChange={e => setLockLocation(e.target.checked)} />
                    Lock Location for all rows
                  </label>
                </div>
              </div>

              {lockLocation && (
                <div className="locked-location-panel">
                  <div className="form-group">
                    <label>Locked Location</label>
                    <input 
                      list="bulk-loc-list" 
                      value={lockedLocation.name} 
                      onChange={e => {
                        const loc = locations.find(l => l.name === e.target.value);
                        setLockedLocation({ id: loc ? loc.id : '', name: e.target.value });
                        setBulkRecords(bulkRecords.map(r => ({ ...r, location_id: loc ? loc.id : '', location_search: e.target.value })));
                      }} 
                      placeholder="Select location to lock..."
                    />
                  </div>
                </div>
              )}

              <div className="table-container minimal">
                <table>
                  <thead>
                    <tr>
                      {!lockLocation && <th>Location</th>}
                      <th>Employee</th>
                      <th>Deaths</th>
                      <th>Feeds</th>
                      <th>Notes</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRecords.map((r, i) => (
                      <tr key={i}>
                        {!lockLocation && (
                          <td>
                            <input 
                              list="bulk-loc-list" 
                              value={r.location_search} 
                              onChange={e => handleBulkInputChange(i, 'location_search', e.target.value)} 
                              required 
                            />
                          </td>
                        )}
                        <td>
                          <input 
                            list="bulk-emp-list" 
                            value={r.employee_search} 
                            onChange={e => handleBulkInputChange(i, 'employee_search', e.target.value)} 
                            required 
                          />
                        </td>
                        <td><input type="number" value={r.deaths} onChange={e => handleBulkInputChange(i, 'deaths', e.target.value)} required /></td>
                        <td><input type="number" value={r.feeds} onChange={e => handleBulkInputChange(i, 'feeds', e.target.value)} required /></td>
                        <td><input type="text" value={r.notes} onChange={e => handleBulkInputChange(i, 'notes', e.target.value)} placeholder="Opt. notes" /></td>
                        <td>
                          <button type="button" className="cancel-btn sm" onClick={() => removeBulkRow(i)} title="Remove row">×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <datalist id="bulk-loc-list">{locations.map(l => <option key={l.id} value={l.name} />)}</datalist>
              <datalist id="bulk-emp-list">{employees.map(e => <option key={e.id} value={`${e.first_name} ${e.last_name}`} />)}</datalist>

              <div className="bulk-footer-actions">
                <button type="button" className="action-btn" onClick={addBulkRow}>+ Add Row</button>
                <div className="form-actions">
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Save All Records'}</button>
                  <button type="button" className="secondary-btn" onClick={() => { setShowRecordModal(false); setBulkRecords([{ location_id: '', location_search: '', employee_id: '', employee_search: '', deaths: '', feeds: '', notes: '' }]); setError(''); }}>Cancel</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingMortality && (
        <div className="modal" onClick={() => setEditingMortality(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Edit size={24} />
                <h3>Edit Mortality Record</h3>
              </div>
              <button className="close-btn" onClick={() => setEditingMortality(null)}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={editingMortality.date.slice(0, 10)} onChange={e => setEditingMortality({...editingMortality, date: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input list="bulk-loc-list" value={editingMortality.location_search} onChange={e => {
                  const loc = locations.find(l => l.name === e.target.value);
                  setEditingMortality({...editingMortality, location_id: loc ? loc.id : '', location_search: e.target.value});
                }} required />
              </div>
              <div className="form-group">
                <label>Employee</label>
                <input list="bulk-emp-list" value={editingMortality.employee_search} onChange={e => {
                  const emp = employees.find(emp => `${emp.first_name} ${emp.last_name}` === e.target.value);
                  setEditingMortality({...editingMortality, employee_id: emp ? emp.id : '', employee_search: e.target.value});
                }} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Deaths</label>
                  <input type="number" value={editingMortality.number_of_deaths} onChange={e => setEditingMortality({...editingMortality, number_of_deaths: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Feeds</label>
                  <input type="number" value={editingMortality.number_of_feeds} onChange={e => setEditingMortality({...editingMortality, number_of_feeds: e.target.value})} required />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input type="text" value={editingMortality.notes || ''} onChange={e => setEditingMortality({...editingMortality, notes: e.target.value})} />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={editingMortality.archived} 
                    onChange={e => setEditingMortality({...editingMortality, archived: e.target.checked})} 
                  />
                  Archived
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Update Record'}</button>
                <button type="button" className="secondary-btn" onClick={() => setEditingMortality(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        title="Delete Mortality Record"
        message="Are you sure you want to delete this record? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />

      <ConfirmationModal
        isOpen={archiveConfirm.isOpen}
        title={archiveConfirm.archived ? 'Archive Records' : 'Restore Records'}
        message={(archiveConfirm.archived
          ? 'Archive '
          : 'Restore ') + (archiveConfirm.ids.length === 1
            ? 'this record?'
            : `these ${archiveConfirm.ids.length} records?`)}
        confirmText={archiveConfirm.archived ? 'Archive' : 'Restore'}
        confirmClass={archiveConfirm.archived ? 'secondary-btn' : 'submit-btn'}
        onConfirm={performArchive}
        onCancel={() => setArchiveConfirm({ isOpen: false, ids: [], archived: false })}
      />
    </div>
  );
}

export default Mortality;
